import { useState, useMemo, useEffect } from "react";
import { Calculator, Info, Home, TrendingUp, Handshake, MessageCircle } from "lucide-react";
import { APARTMENTS } from "@/data/apartments";
import { useT } from "@/i18n/LanguageProvider";

const fmtEUR = (n) => Math.round(n).toLocaleString("de-DE") + " €";

const parseSize = (s) =>
    typeof s === "number" ? s : parseFloat(String(s).replace(",", "."));

/**
 * Calculate the equity amount required so that the annuity rate equals
 * (or is just covered by) the monthly cold rent. Rounded up to the next
 * 5.000 € so the slider step stays clean and the result is always
 * cash-flow-positive (not exactly zero).
 */
const equityForBreakEven = (price, parking, rentPerM2, sizeM2, ratePct, years) => {
    const totalCost = (price + parking) * 1.075;            // incl. 7.5 % closing costs
    const targetRate = rentPerM2 * sizeM2;                  // monthly cold rent
    const r = ratePct / 100 / 12;
    const months = years * 12;
    const annFactor = r / (1 - Math.pow(1 + r, -months));   // payment per unit principal
    const financeNeeded = targetRate / annFactor;           // max debt we can carry
    const equity = totalCost - financeNeeded;
    return Math.max(0, Math.ceil(equity / 5000) * 5000);
};

const DEFAULTS = {
    aptId: "erdgeschoss",
    rentPerM2: 18,                                          // project average (€/m² cold)
    ratePct: 3.6,
    years: 30,
};

const DEFAULT_APT = APARTMENTS.find((a) => a.id === DEFAULTS.aptId) || APARTMENTS[0];
const DEFAULT_SIZE = parseSize(DEFAULT_APT.size);
const DEFAULT_EQUITY = equityForBreakEven(
    DEFAULT_APT.priceValue,
    DEFAULT_APT.parkingPrice,
    DEFAULTS.rentPerM2,
    DEFAULT_SIZE,
    DEFAULTS.ratePct,
    DEFAULTS.years,
);

export const MortgageCalculator = () => {
    const t = useT();
    const [mode, setMode] = useState("eigenheim"); // "eigenheim" | "kapitalanlage"
    const [aptId, setAptId] = useState(DEFAULTS.aptId); // "erdgeschoss" | ... | "custom"
    const [price, setPrice] = useState(DEFAULT_APT.priceValue);
    const [parking, setParking] = useState(DEFAULT_APT.parkingPrice);
    const [equity, setEquity] = useState(DEFAULT_EQUITY);
    const [rate, setRate] = useState(DEFAULTS.ratePct);
    const [years, setYears] = useState(DEFAULTS.years);
    // investment-mode extras
    const [rentPerM2, setRentPerM2] = useState(DEFAULTS.rentPerM2);
    const [sizeM2, setSizeM2] = useState(DEFAULT_SIZE);

    // Click on a preset apartment tab → copy its price & size and
    // re-calculate the equity so that break-even is reached immediately.
    const selectApartment = (id) => {
        const apt = APARTMENTS.find((a) => a.id === id);
        if (!apt) {
            setAptId("custom");
            return;
        }
        setAptId(id);
        const aptSize = parseSize(apt.size);
        setPrice(apt.priceValue);
        setParking(apt.parkingPrice);
        setSizeM2(aptSize);
        setEquity(
            equityForBreakEven(apt.priceValue, apt.parkingPrice, rentPerM2, aptSize, rate, years),
        );
    };

    // Listen for sync events from the rental-forecast calculator: prefills
    // purchase price, size, cold rent/m² and switches to Kapitalanlage mode.
    // A short highlight flash on the result column confirms the transfer.
    const [synced, setSynced] = useState(false);
    useEffect(() => {
        const handler = (e) => {
            const d = e.detail || {};
            if (typeof d.price === "number") setPrice(d.price);
            if (typeof d.parking === "number") setParking(d.parking);
            if (typeof d.sizeM2 === "number") setSizeM2(d.sizeM2);
            if (typeof d.rentPerM2 === "number") setRentPerM2(d.rentPerM2);
            if (typeof d.apartmentId === "string" && APARTMENTS.some((a) => a.id === d.apartmentId)) {
                setAptId(d.apartmentId);
            }
            setMode("kapitalanlage");
            setSynced(true);
            window.setTimeout(() => setSynced(false), 2200);
        };
        window.addEventListener("g21:sync-financing", handler);
        return () => window.removeEventListener("g21:sync-financing", handler);
    }, []);

    const totalPrice = price + parking;
    const additional = totalPrice * 0.075;
    const financeNeeded = Math.max(0, totalPrice + additional - equity);

    const result = useMemo(() => {
        if (financeNeeded <= 0) {
            return { monthly: 0, totalInterest: 0, residual: 0 };
        }
        const months = years * 12;
        const r = rate / 100 / 12;
        const monthly = (financeNeeded * r) / (1 - Math.pow(1 + r, -months));
        let balance = financeNeeded;
        let totalInterest = 0;
        for (let i = 0; i < months; i++) {
            const interest = balance * r;
            const principal = monthly - interest;
            totalInterest += interest;
            balance -= principal;
        }
        return { monthly, totalInterest, residual: Math.max(0, balance) };
    }, [financeNeeded, rate, years]);

    const isInvest = mode === "kapitalanlage";
    const monthlyRent = rentPerM2 * sizeM2;                 // gross kaltmiete
    const monthlyCashflow = monthlyRent - result.monthly;   // pre-tax, simplified
    const annualRent = monthlyRent * 12;
    const grossYield = totalPrice > 0 ? (annualRent / totalPrice) * 100 : 0;

    // Break-even year: when does the indexed cold rent cover the fixed monthly rate?
    // Assumption: +15 % every 3 years (standard A-location Neubau indexing).
    // Returns the earliest full 3-year step at which rent >= rate.
    const breakEven = useMemo(() => {
        if (result.monthly <= 0) return { reached: null, year: null, step: null };
        if (monthlyRent >= result.monthly) return { reached: true, year: 0, step: 0 };
        const factorNeeded = result.monthly / monthlyRent;
        const steps = Math.ceil(Math.log(factorNeeded) / Math.log(1.15));
        const year = steps * 3;
        if (year > 30) return { reached: false, year: null, step: null };
        return { reached: true, year, step: steps };
    }, [monthlyRent, result.monthly]);

    // Broadcast current financing context so the rental-forecast calculator
    // can mark the break-even row in its timeline. Debounced via setTimeout
    // to avoid event-spam while sliders are being dragged.
    useEffect(() => {
        const id = window.setTimeout(() => {
            window.dispatchEvent(
                new CustomEvent("g21:financing-rate", {
                    detail: {
                        monthly: result.monthly,
                        rentPerM2,
                        sizeM2,
                        mode,
                    },
                }),
            );
        }, 120);
        return () => window.clearTimeout(id);
    }, [result.monthly, rentPerM2, sizeM2, mode]);

    return (
        <section
            id="finanzierung"
            className="bg-cream py-24 sm:py-32"
            data-testid="mortgage-calculator-section"
        >
            <div className="container-page">
                <div className="grid lg:grid-cols-12 gap-10 mb-12">
                    <div className="lg:col-span-5">
                        <span className="overline">
                            {t({ de: "07 / 12 — Finanzierung & KfW", en: "07 / 12 — Financing & KfW" })}
                        </span>
                        <h2 className="h-section mt-4 text-balance">
                            {t({ de: "Was kostet das Wohnen", en: "What does living" })}
                            <span className="italic text-brand-500"> {t({ de: "wirklich?", en: "actually cost?" })}</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <p className="body-md">
                            {t({
                                de: (
                                    <>
                                        Eine schnelle Beispielrechnung – wahlweise für das <strong>Eigenheim</strong> oder als{" "}
                                        <strong>Kapitalanlage</strong>. Inklusive ca. <strong>7,5 % Kaufnebenkosten</strong> (6 % Grunderwerbsteuer
                                        Hessen + ca. 1,5 % Notar &amp; Grundbuch · maklerfrei direkt vom Bauträger). Ergebnisse sind
                                        unverbindlich – bitte sprechen Sie für eine konkrete Finanzierung mit unserem Finanzierungspartner.
                                    </>
                                ),
                                en: (
                                    <>
                                        A quick example calculation – either for an <strong>owner-occupied home</strong> or as an{" "}
                                        <strong>investment</strong>. Includes approx. <strong>7.5 % closing costs</strong> (6 % real-estate
                                        transfer tax in Hesse + approx. 1.5 % notary &amp; land register · brokerage-free, direct from the
                                        developer). Results are non-binding – please speak with our financing partner for an actual quote.
                                    </>
                                ),
                            })}
                        </p>
                    </div>
                </div>

                {/* Mode toggle */}
                <div className="inline-flex rounded-full overflow-hidden border border-stone-200 bg-white mb-8" role="tablist" data-testid="mortgage-mode-toggle">
                    <ModeButton
                        active={mode === "eigenheim"}
                        onClick={() => setMode("eigenheim")}
                        icon={<Home size={16} />}
                        label={t({ de: "Eigenheim", en: "Owner-occupied" })}
                        testid="mortgage-mode-eigenheim"
                    />
                    <ModeButton
                        active={mode === "kapitalanlage"}
                        onClick={() => setMode("kapitalanlage")}
                        icon={<TrendingUp size={16} />}
                        label={t({ de: "Kapitalanlage", en: "Investment" })}
                        testid="mortgage-mode-kapitalanlage"
                    />
                </div>

                {/* Apartment preset selector */}
                <div className="mb-8" data-testid="mortgage-apt-selector">
                    <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
                        <span className="overline text-brand-500">
                            {t({ de: "Wohnung vorauswählen", en: "Preselect apartment" })}
                        </span>
                        <span className="font-body text-xs text-stone-500">
                            {t({
                                de: "Setzt Preis & Wohnfläche – Eigenkapital wird so gewählt, dass die Kaltmiete die Rate deckt.",
                                en: "Sets price & size – equity is chosen so cold rent already covers the rate.",
                            })}
                        </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {APARTMENTS.map((a) => (
                            <button
                                key={a.id}
                                type="button"
                                onClick={() => selectApartment(a.id)}
                                className={`px-4 py-2 text-sm border transition-colors ${
                                    aptId === a.id
                                        ? "border-brand-500 bg-brand-500 text-white"
                                        : "border-stone-200 bg-white text-ink hover:border-brand-500"
                                }`}
                                aria-pressed={aptId === a.id}
                                data-testid={`mortgage-apt-${a.id}`}
                            >
                                <span className="font-heading">{a.shortName || a.name}</span>
                                <span className="hidden sm:inline ml-2 text-xs opacity-80">{a.size} m²</span>
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={() => setAptId("custom")}
                            className={`px-4 py-2 text-sm border transition-colors ${
                                aptId === "custom"
                                    ? "border-brand-500 bg-brand-500 text-white"
                                    : "border-stone-200 bg-white text-ink hover:border-brand-500"
                            }`}
                            aria-pressed={aptId === "custom"}
                            data-testid="mortgage-apt-custom"
                        >
                            <span className="font-heading">{t({ de: "Frei wählbar", en: "Custom" })}</span>
                        </button>
                    </div>
                </div>

                <div className="grid lg:grid-cols-2 gap-px bg-stone-200 border border-stone-200 rounded-2xl overflow-hidden">
                    {/* Inputs */}
                    <div className="bg-white p-8 lg:p-10 space-y-7" data-testid="mortgage-inputs">
                        <SliderField
                            label={t({ de: "Kaufpreis Wohnung", en: "Purchase price" })}
                            value={price}
                            min={400000}
                            max={1000000}
                            step={1000}
                            onChange={(v) => { setPrice(v); setAptId("custom"); }}
                            unit={fmtEUR(price)}
                            testid="mortgage-price"
                        />
                        <SliderField
                            label={t({ de: "Stellplatz", en: "Parking" })}
                            value={parking}
                            min={0}
                            max={50000}
                            step={1000}
                            onChange={setParking}
                            unit={fmtEUR(parking)}
                            testid="mortgage-parking"
                        />
                        <SliderField
                            label={t({ de: "Eigenkapital", en: "Equity" })}
                            value={equity}
                            min={0}
                            max={600000}
                            step={5000}
                            onChange={setEquity}
                            unit={fmtEUR(equity)}
                            testid="mortgage-equity"
                        />
                        <SliderField
                            label={t({ de: "Sollzinssatz p.a.", en: "Interest rate p.a." })}
                            value={rate}
                            min={1}
                            max={6}
                            step={0.1}
                            onChange={setRate}
                            unit={`${rate.toFixed(1)} %`}
                            testid="mortgage-rate"
                        />
                        <SliderField
                            label={t({ de: "Laufzeit", en: "Term" })}
                            value={years}
                            min={10}
                            max={40}
                            step={1}
                            onChange={setYears}
                            unit={`${years} ${t({ de: "Jahre", en: "years" })}`}
                            testid="mortgage-years"
                        />

                        {isInvest && (
                            <div className="pt-6 mt-2 border-t border-stone-200 space-y-7" data-testid="mortgage-invest-inputs">
                                <div>
                                    <span className="overline text-brand-500">
                                        {t({ de: "Vermietungs-Annahmen", en: "Rental assumptions" })}
                                    </span>
                                </div>
                                <SliderField
                                    label={t({ de: "Wohnfläche", en: "Living area" })}
                                    value={sizeM2}
                                    min={50}
                                    max={150}
                                    step={1}
                                    onChange={(v) => { setSizeM2(v); setAptId("custom"); }}
                                    unit={`${sizeM2} m²`}
                                    testid="mortgage-size"
                                />
                                <SliderField
                                    label={t({ de: "Kaltmiete / m²", en: "Cold rent / sqm" })}
                                    value={rentPerM2}
                                    min={12}
                                    max={24}
                                    step={0.5}
                                    onChange={setRentPerM2}
                                    unit={`${rentPerM2.toFixed(1)} €`}
                                    testid="mortgage-rent-per-m2"
                                />
                            </div>
                        )}
                    </div>

                    {/* Results */}
                    <div
                        className={`bg-ink text-white p-8 lg:p-10 flex flex-col justify-between transition-shadow duration-500 ${
                            synced ? "ring-2 ring-clay-300" : ""
                        }`}
                        data-testid="mortgage-results"
                    >
                        <div>
                            <div className="flex items-center gap-2 mb-6">
                                <Calculator size={18} className="text-clay-300" />
                                <span className="overline text-clay-300">
                                    {isInvest
                                        ? t({ de: "Beispielrechnung · Kapitalanlage", en: "Example · investment" })
                                        : t({ de: "Beispielrechnung · Eigenheim", en: "Example · owner-occupied" })}
                                </span>
                            </div>

                            <div className="space-y-4">
                                <ResultRow
                                    label={t({ de: "Gesamtkosten (inkl. ca. 7,5 % Nebenkosten)", en: "Total cost (incl. approx. 7.5 % closing)" })}
                                    value={fmtEUR(totalPrice + additional)}
                                />
                                <ResultRow
                                    label={t({ de: "Davon Nebenkosten (7,5 %)", en: "Of which closing costs (7.5 %)" })}
                                    value={fmtEUR(additional)}
                                    muted
                                />
                                <ResultRow
                                    label={t({ de: "Finanzierungsbedarf", en: "Financing required" })}
                                    value={fmtEUR(financeNeeded)}
                                />
                            </div>

                            <div className="mt-10 pt-6 border-t border-white/15">
                                <span className="overline text-clay-300">
                                    {t({ de: "Monatliche Rate", en: "Monthly payment" })}
                                </span>
                                <div className="font-heading text-5xl text-clay-300 mt-2" data-testid="mortgage-monthly">
                                    {financeNeeded > 0 ? fmtEUR(result.monthly) : "0 €"}
                                </div>
                                <div className="font-body text-sm text-white/70 mt-1">
                                    {t({ de: "bei", en: "at" })} {rate.toFixed(1)} % {t({ de: "über", en: "over" })} {years}{" "}
                                    {t({ de: "Jahre Laufzeit", en: "years term" })}
                                </div>
                            </div>

                            {isInvest && (
                                <div className="mt-8 space-y-3" data-testid="mortgage-invest-results">
                                    <ResultRow
                                        label={t({ de: "Mietpotential (kalt)", en: "Rental potential (cold)" })}
                                        value={`${fmtEUR(monthlyRent)} / Mo.`}
                                    />
                                    <ResultRow
                                        label={t({ de: "Brutto-Mietrendite (kalt)", en: "Gross rental yield (cold)" })}
                                        value={`${grossYield.toFixed(2)} %`}
                                        muted
                                    />
                                    <div className="pt-4 border-t border-white/15">
                                        <span className="overline text-clay-300">
                                            {t({ de: "Monatlicher Cashflow (vor Steuer)", en: "Monthly cashflow (pre-tax)" })}
                                        </span>
                                        <div
                                            className={`font-heading text-4xl mt-2 ${
                                                monthlyCashflow >= 0 ? "text-emerald-300" : "text-rose-300"
                                            }`}
                                            data-testid="mortgage-cashflow"
                                        >
                                            {monthlyCashflow >= 0 ? "+" : ""}
                                            {fmtEUR(monthlyCashflow)}
                                        </div>
                                        <div className="font-body text-xs text-white/70 mt-1">
                                            {t({
                                                de: "Kaltmiete abzgl. Annuität – Betriebskosten sind umlagefähig, AfA-Steuereffekt, Rücklagen & Leerstand sind nicht berücksichtigt.",
                                                en: "Cold rent minus annuity – operating costs are passed to tenants; depreciation tax effect, reserves & vacancy are not included.",
                                            })}
                                        </div>
                                        <a
                                            href="#mietrendite"
                                            className="mt-4 inline-flex items-center gap-1 text-xs font-body text-clay-300 underline decoration-clay-300/50 underline-offset-2 hover:text-white"
                                            data-testid="link-to-rental-yield"
                                        >
                                            {t({
                                                de: "Mietprognose für alle 4 Wohnungen inkl. Entwicklung bis 30 Jahre →",
                                                en: "Rent forecast for all 4 apartments incl. 30-year outlook →",
                                            })}
                                        </a>
                                        <a
                                            href="/vermoegensaufbau"
                                            className="mt-2 inline-flex items-center gap-1 text-xs font-body text-clay-300 underline decoration-clay-300/50 underline-offset-2 hover:text-white"
                                            data-testid="link-to-wealth"
                                        >
                                            {t({
                                                de: "Vermögensaufbau-Rechner mit allen EK-Szenarien (50 k bis Break-Even) →",
                                                en: "Wealth-building calculator with all equity scenarios (€50 k to break-even) →",
                                            })}
                                        </a>
                                    </div>

                                    {/* Break-even indicator */}
                                    {breakEven.year !== null && breakEven.year > 0 && breakEven.reached && (
                                        <div className="mt-6 p-4 bg-emerald-400/10 border border-emerald-400/30" data-testid="break-even-reached">
                                            <div className="flex items-center gap-2">
                                                <TrendingUp size={16} className="text-emerald-300" />
                                                <span className="overline text-emerald-300">
                                                    {t({ de: "Break-Even · Mietentwicklung", en: "Break-even · rent growth" })}
                                                </span>
                                            </div>
                                            <div className="font-heading text-3xl text-emerald-200 mt-2">
                                                {t({ de: "Ab Jahr", en: "From year" })} {breakEven.year}
                                            </div>
                                            <p className="font-body text-xs text-white/75 mt-1 leading-relaxed">
                                                {t({
                                                    de: (
                                                        <>
                                                            Bei +15 % Mietsteigerung alle 3 Jahre deckt die <strong>Kaltmiete</strong> die heutige
                                                            monatliche Rate von {fmtEUR(result.monthly)} – ab dann positiver Cashflow,
                                                            ohne Mieteinnahmen zu erhöhen.
                                                        </>
                                                    ),
                                                    en: (
                                                        <>
                                                            With +15 % rent indexing every 3 years, the <strong>cold rent</strong> covers today's
                                                            monthly rate of {fmtEUR(result.monthly)} – positive cashflow from that point on,
                                                            without raising rent further.
                                                        </>
                                                    ),
                                                })}
                                            </p>
                                        </div>
                                    )}

                                    {breakEven.year === 0 && breakEven.reached && (
                                        <div className="mt-6 p-4 bg-emerald-400/10 border border-emerald-400/30" data-testid="break-even-today">
                                            <div className="flex items-center gap-2">
                                                <TrendingUp size={16} className="text-emerald-300" />
                                                <span className="overline text-emerald-300">
                                                    {t({ de: "Positiver Cashflow ab Tag 1", en: "Positive cashflow from day 1" })}
                                                </span>
                                            </div>
                                            <p className="font-body text-xs text-white/75 mt-2 leading-relaxed">
                                                {t({
                                                    de: "Die Kaltmiete deckt die Rate bereits heute. Tilgung erfolgt ohne eigene Zuzahlung.",
                                                    en: "Cold rent already covers the rate. Repayment works without out-of-pocket top-up.",
                                                })}
                                            </p>
                                        </div>
                                    )}

                                    {breakEven.reached === false && (
                                        <div className="mt-6 p-4 bg-white/5 border border-white/20" data-testid="break-even-missed">
                                            <div className="flex items-center gap-2">
                                                <Info size={16} className="text-clay-300" />
                                                <span className="overline text-clay-300">
                                                    {t({ de: "Break-Even · Hinweis", en: "Break-even · note" })}
                                                </span>
                                            </div>
                                            <p className="font-body text-xs text-white/75 mt-2 leading-relaxed">
                                                {t({
                                                    de: "Mit der aktuellen Kombination wird die Rate auch nach 30 Jahren Mietsteigerung nicht allein durch die Kaltmiete gedeckt. Tipp: mehr Eigenkapital, geringerer Zinssatz oder längere Tilgung.",
                                                    en: "With this combination, even 30 years of rent indexing won't cover the rate from cold rent alone. Tip: more equity, lower interest or longer term.",
                                                })}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {!isInvest && financeNeeded > 0 && (
                                <div className="mt-6 grid grid-cols-2 gap-4">
                                    <ResultRow
                                        label={t({ de: "Zinsen gesamt", en: "Total interest" })}
                                        value={fmtEUR(result.totalInterest)}
                                        muted
                                        compact
                                    />
                                    <ResultRow
                                        label={t({ de: "Restschuld nach Laufzeit", en: "Residual debt at end of term" })}
                                        value={fmtEUR(result.residual)}
                                        muted
                                        compact
                                    />
                                </div>
                            )}
                        </div>

                        <div className="mt-8 flex items-start gap-3 p-4 bg-clay-300/10 border border-clay-300/30">
                            <Info size={16} className="text-clay-300 shrink-0 mt-0.5" />
                            <p className="font-body text-xs text-white/80 leading-relaxed">
                                <strong className="text-white">{t({ de: "Tipp:", en: "Tip:" })}</strong>{" "}
                                {isInvest
                                    ? t({
                                          de: (
                                              <>
                                                  Für Kapitalanleger ist die <strong>5 % degressive AfA</strong> für Neubau-Effizienzhäuser ab 2026
                                                  besonders attraktiv. Zusätzlich: <strong>KfW-Zuschüsse</strong> für klimafreundlichen Neubau.
                                              </>
                                          ),
                                          en: (
                                              <>
                                                  Investors benefit from <strong>5 % declining-balance depreciation</strong> for new-build energy-40 homes
                                                  from 2026. Plus <strong>KfW subsidies</strong> for climate-friendly new builds.
                                              </>
                                          ),
                                      })
                                    : t({
                                          de: (
                                              <>
                                                  Bei Effizienzhaus 40 ist die <strong>KfW-Förderung</strong> für klimafreundlichen Neubau möglich.
                                                  Dadurch kann sich Ihr effektiver Zinssatz nochmal deutlich reduzieren.
                                              </>
                                          ),
                                          en: (
                                              <>
                                                  For energy class 40 the <strong>KfW subsidy</strong> for climate-friendly new builds is available,
                                                  which can lower your effective rate noticeably.
                                              </>
                                          ),
                                      })}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Financing partner block */}
                <div className="mt-14 grid lg:grid-cols-12 gap-10 items-start" data-testid="financing-partner">
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-3">
                            <Handshake size={22} className="text-brand-500" />
                            <span className="overline text-brand-500">
                                {t({ de: "Finanzierung mit Partner", en: "Financing with a partner" })}
                            </span>
                        </div>
                        <h3 className="font-heading text-3xl sm:text-4xl text-ink mt-4 text-balance">
                            {t({ de: "Wir arbeiten mit einem", en: "We work with a" })}
                            <span className="italic text-brand-500"> {t({ de: "erfahrenen Finanzierungspartner.", en: "seasoned financing partner." })}</span>
                        </h3>
                    </div>
                    <div className="lg:col-span-7 space-y-6">
                        <p className="body-md">
                            {t({
                                de: (
                                    <>
                                        Ob <strong>klassische Baufinanzierung fürs Eigenheim</strong> oder <strong>tragfähige
                                        Kapitalanlage für den Vermögensaufbau</strong> – wir vermitteln Sie an unseren unabhängigen
                                        Finanzierungsexperten. Er vergleicht Angebote mehrerer Banken, berücksichtigt KfW-Zuschüsse
                                        und strukturiert die Finanzierung passend zu Ihrer Lebenslage.
                                    </>
                                ),
                                en: (
                                    <>
                                        Whether you need a <strong>classic home loan</strong> or a <strong>solid investment-property
                                        financing</strong> for long-term wealth building – we connect you to our independent
                                        financing expert. He compares offers from multiple banks, integrates KfW subsidies and
                                        structures the loan to fit your life situation.
                                    </>
                                ),
                            })}
                        </p>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <PartnerPill
                                icon={<Home size={16} className="text-brand-500" />}
                                title={t({ de: "Eigenheim-Finanzierung", en: "Home financing" })}
                                body={t({
                                    de: "Annuität, KfW-40-Zuschuss, Sondertilgung – abgestimmt auf Ihr Einkommen.",
                                    en: "Annuity loan, KfW-40 grant, special repayments – tailored to your income.",
                                })}
                            />
                            <PartnerPill
                                icon={<TrendingUp size={16} className="text-brand-500" />}
                                title={t({ de: "Kapitalanlage-Finanzierung", en: "Investment financing" })}
                                body={t({
                                    de: "Strukturierung mit 5 % AfA, Cashflow-Optimierung, Vollvermietungs-Szenarien.",
                                    en: "Structuring with 5 % depreciation, cashflow optimisation, full-occupancy scenarios.",
                                })}
                            />
                        </div>
                        <a
                            href="#kontakt"
                            className="btn-primary inline-flex"
                            data-testid="financing-partner-cta"
                        >
                            <MessageCircle size={16} />
                            {t({ de: "Beratungstermin anfragen", en: "Request a consultation" })}
                        </a>
                        <p className="font-body text-xs text-stone-500 max-w-xl">
                            {t({
                                de: "Die Vermittlung erfolgt unverbindlich und kostenfrei. Alle Finanzierungsangebote werden ausschließlich zwischen Ihnen und der jeweiligen Bank abgeschlossen.",
                                en: "Referral is non-binding and free of charge. All financing contracts are concluded exclusively between you and the respective bank.",
                            })}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
};

const ModeButton = ({ active, onClick, icon, label, testid }) => (
    <button
        type="button"
        onClick={onClick}
        className={`px-5 py-3 text-sm inline-flex items-center gap-2 transition-colors ${
            active ? "bg-ink text-white" : "bg-white text-ink hover:bg-stone-100"
        }`}
        role="tab"
        aria-selected={active}
        data-testid={testid}
    >
        {icon}
        {label}
    </button>
);

const PartnerPill = ({ icon, title, body }) => (
    <div className="rounded-2xl border border-stone-200 bg-white p-5">
        <div className="flex items-center gap-2">
            {icon}
            <span className="font-heading text-lg text-ink">{title}</span>
        </div>
        <p className="body-md text-sm text-stone-600 mt-2">{body}</p>
    </div>
);

const SliderField = ({ label, value, min, max, step, onChange, unit, testid }) => (
    <div data-testid={testid}>
        <div className="flex items-baseline justify-between mb-2">
            <label className="label-line">{label}</label>
            <span className="font-heading text-xl text-ink">{unit}</span>
        </div>
        <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => onChange(parseFloat(e.target.value))}
            className="w-full h-1 appearance-none bg-stone-200 cursor-pointer accent-brand-500"
        />
        <div className="flex justify-between mt-1 text-[10px] font-body text-stone-400 tracking-wide">
            <span>{min.toLocaleString("de-DE")}</span>
            <span>{max.toLocaleString("de-DE")}</span>
        </div>
    </div>
);

const ResultRow = ({ label, value, muted = false, compact = false }) => (
    <div className={compact ? "" : "flex items-baseline justify-between gap-3"}>
        <span className={`font-body text-xs uppercase tracking-[0.15em] ${muted ? "text-white/55" : "text-white/85"}`}>
            {label}
        </span>
        <span className={`font-heading ${compact ? "block text-lg mt-0.5" : ""} ${muted ? "text-white/80" : "text-white"} ${compact ? "" : "text-2xl"}`}>
            {value}
        </span>
    </div>
);
